import { AsyncTask, ResourceLock, sleep } from '@pulse/common';
import { NavigatorLockManager, NavigatorLockRequestOptions, PulseEnvironment } from '../../globals';
import { UUIDService } from '../uuid.service';

import { LockManager } from './lock-manager';

interface NativeResourceLock extends ResourceLock {
    task: AsyncTask<void>;
}

export class NativeLockManager implements LockManager {
    private lockManager?: NavigatorLockManager;
    private locks = new Map<string, NativeResourceLock>();

    constructor(
        private env: PulseEnvironment,
        private uuid: UUIDService,
    ) {
        this.lockManager = env.win.navigator.locks;
    }

    async supported(): Promise<boolean> {
        let isSafe = false;
        try {
            if (this.lockManager?.query) {
                await this.lockManager?.query();
                isSafe = true;
            }
        } catch {
            isSafe = false;
        }

        return (
            typeof this.lockManager?.request === 'function' &&
            typeof this.lockManager?.query === 'function' &&
            typeof this.env.win.AbortController === 'function' &&
            isSafe
        );
    }

    async locked(name: string): Promise<boolean> {
        if (!this.lockManager) {
            throw new Error('Native LockManager not supported!');
        }

        const state = await this.lockManager.query();

        return state.held.some((i) => i.name === name);
    }

    request(name: string, timeout = 1000): Promise<ResourceLock | null> {
        if (!this.lockManager) {
            throw new Error('Native LockManager not supported!');
        }

        const task = new AsyncTask<ResourceLock | null>();
        const handlerTask = new AsyncTask<void>();
        const controller = new AbortController();
        const options: NavigatorLockRequestOptions = { signal: controller.signal };
        let acquired = false;

        this.lockManager
            .request(name, options, () => {
                acquired = true;

                const lock: NativeResourceLock = {
                    name,
                    id: this.uuid.generate(),
                    clientId: '',
                    createdDate: 0,
                    task: handlerTask,
                };

                this.locks.set(name, lock);
                task.resolve(lock);

                return handlerTask.waitUntilResolved();
            })
            .catch(() => task.resolve(null));

        sleep(timeout)
            .then(() => {
                if (acquired) {
                    return;
                }

                controller.abort();
                task.resolve(null);
            })
            .catch((err) => {
                console.error(err);
            });

        return task.waitUntilResolved();
    }

    async release(lock: ResourceLock): Promise<void> {
        if (!this.lockManager) {
            throw new Error('Native LockManager not supported!');
        }

        const acquiredLock = this.locks.get(lock.name);

        if (!acquiredLock || acquiredLock.id !== lock.id) {
            return;
        }

        acquiredLock.task.resolve();
        this.locks.delete(acquiredLock.name);
    }
}
